import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, UserPlus, Shield, Crown, Trash2, Plus, Mail, Loader2, X, Check, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { organizationApi } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const roleBadge = (role) => role === 'admin'
  ? { label: 'Admin', bg: 'rgba(105,148,198,0.14)', color: '#4f74a0', Icon: Crown }
  : { label: 'Member', bg: 'rgba(120,130,145,0.12)', color: '#6b7280', Icon: Users };

function Avatar({ name, picture }) {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return picture
    ? <img src={picture} alt={name} className="w-8 h-8 rounded-full object-cover" />
    : <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0" style={{ background: '#6994c6' }}>{initials}</div>;
}

const STATS = [
  { key: 'open', label: 'Open', color: '#4f74a0' },
  { key: 'overdue', label: 'Overdue', color: '#B4332E' },
  { key: 'due_this_week', label: 'Due this week', color: '#6994c6' },
  { key: 'completed', label: 'Completed', color: '#3f8f6b' },
];

function MetricsStrip({ m, testId }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={testId}>
      {STATS.map(s => (
        <div key={s.key} className="p-3 rounded-xl border border-border bg-white" data-testid={`stat-${s.key}`}>
          <div className="text-2xl font-semibold" style={{ color: s.color }}>{m[s.key] ?? 0}</div>
          <div className="text-[11px] text-muted-foreground">{s.label}</div>
        </div>
      ))}
      <div className="p-3 rounded-xl border border-border bg-white" data-testid="stat-completion">
        <div className="text-2xl font-semibold" style={{ color: '#3f8f6b' }}>{m.completion_rate ?? 0}%</div>
        <div className="text-[11px] text-muted-foreground">Completion</div>
      </div>
    </div>
  );
}

export default function PeopleTeamsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);          // { company, global_role, seats_used }
  const [pending, setPending] = useState([]);    // invites addressed to me
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [report, setReport] = useState(null);
  const [reportError, setReportError] = useState(false);

  const [orgName, setOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', global_role: 'member', team_id: '' });
  const [inviting, setInviting] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', allow_member_cross_visibility: false });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);

  const isAdmin = org?.global_role === 'admin';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await organizationApi.get();
      setOrg(res.data);
      if (res.data.company) {
        const [m, t] = await Promise.all([organizationApi.members(), organizationApi.teams()]);
        setMembers(m.data.members || []);
        setTeams(t.data.teams || []);
        if (res.data.global_role === 'admin') {
          const inv = await organizationApi.invites();
          setInvites(inv.data.invites || []);
          organizationApi.companyReport().then(r => { setReport(r.data); setReportError(false); }).catch(() => setReportError(true));
        }
      } else {
        const p = await organizationApi.pendingInvites();
        setPending(p.data.invites || []);
      }
    } catch {
      toast.error('Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createOrg = async () => {
    if (!orgName.trim()) return;
    setCreating(true);
    try {
      await organizationApi.create(orgName.trim());
      toast.success('Organization created — you are the Admin');
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create organization'); }
    finally { setCreating(false); }
  };

  const acceptInvite = async (id) => {
    try { await organizationApi.acceptInvite(id); toast.success('Joined the organization'); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not accept invite'); }
  };

  const sendInvite = async () => {
    if (!inviteForm.email.trim()) return;
    setInviting(true);
    try {
      await organizationApi.invite({ ...inviteForm, team_id: inviteForm.team_id || null });
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setInviteForm({ email: '', global_role: 'member', team_id: '' });
      const inv = await organizationApi.invites();
      setInvites(inv.data.invites || []);
      const res = await organizationApi.get(); setOrg(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send invite'); }
    finally { setInviting(false); }
  };

  const revokeInvite = async (id) => {
    try { await organizationApi.revokeInvite(id); setInvites(prev => prev.filter(i => i.id !== id)); toast.success('Invite revoked'); }
    catch { toast.error('Could not revoke invite'); }
  };

  const createTeam = async () => {
    if (!teamForm.name.trim()) return;
    setCreatingTeam(true);
    try {
      await organizationApi.createTeam(teamForm);
      toast.success('Team created');
      setTeamForm({ name: '', description: '', allow_member_cross_visibility: false });
      setShowTeamForm(false);
      const t = await organizationApi.teams(); setTeams(t.data.teams || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create team'); }
    finally { setCreatingTeam(false); }
  };

  const openTeam = async (id) => {
    try { const res = await organizationApi.getTeam(id); setSelectedTeam(res.data); }
    catch { toast.error('Could not open team'); }
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Archive this team? Members lose team-based visibility.')) return;
    try {
      await organizationApi.deleteTeam(id);
      setTeams(prev => prev.filter(t => t.id !== id));
      setSelectedTeam(null);
      toast.success('Team archived');
    } catch { toast.error('Could not archive team'); }
  };

  const addMember = async (userId, teamRole) => {
    try { const res = await organizationApi.addMember(selectedTeam.id, { user_id: userId, team_role: teamRole }); setSelectedTeam(res.data); toast.success('Member added'); refreshTeamsCount(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not add member'); }
  };

  const removeMember = async (userId) => {
    try { await organizationApi.removeMember(selectedTeam.id, userId); const res = await organizationApi.getTeam(selectedTeam.id); setSelectedTeam(res.data); toast.success('Member removed'); refreshTeamsCount(); }
    catch { toast.error('Could not remove member'); }
  };

  const refreshTeamsCount = async () => { const t = await organizationApi.teams(); setTeams(t.data.teams || []); };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="people-teams-loading"><Loader2 className="animate-spin" style={{ color: '#6994c6' }} /></div>;
  }

  // ── No organization yet ──
  if (!org?.company) {
    return (
      <div className="pagewrap" data-testid="people-teams-page">
        {pending.length > 0 && (
          <div className="mb-8" data-testid="pending-invites">
            <h2 className="text-lg font-semibold mb-3">You've been invited</h2>
            {pending.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-4 rounded-xl border border-border bg-white mb-2" data-testid={`pending-invite-${inv.id}`}>
                <div className="flex items-center gap-3">
                  <Building2 size={18} style={{ color: '#6994c6' }} />
                  <div>
                    <p className="text-sm font-medium">{inv.company_name}</p>
                    <p className="text-xs text-muted-foreground">Role: {inv.global_role}</p>
                  </div>
                </div>
                <button onClick={() => acceptInvite(inv.id)} data-testid={`accept-invite-${inv.id}`}
                  className="text-sm font-medium px-4 h-9 rounded-lg text-white" style={{ background: '#6994c6' }}>Join</button>
              </div>
            ))}
          </div>
        )}
        <div className="text-center py-10 px-6 rounded-2xl border border-border bg-white">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(105,148,198,0.12)' }}>
            <Building2 size={26} style={{ color: '#6994c6' }} />
          </div>
          <h1 className="text-2xl font-semibold mb-2 serif">Create your organization</h1>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Teams give leaders visibility and keep individual work private by default. Create a company, add teams, and invite your people — you'll be the Company Admin.
          </p>
          <div className="flex items-center gap-2 max-w-sm mx-auto">
            <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Organization name"
              data-testid="org-name-input"
              className="flex-1 h-11 rounded-xl border border-border px-3 text-sm outline-none focus:border-[#6994c6]" />
            <button onClick={createOrg} disabled={creating || !orgName.trim()} data-testid="create-org-button"
              className="h-11 px-5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2" style={{ background: '#6994c6' }}>
              {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}Create
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Organization exists ──
  return (
    <div className="pagewrap home-v2" data-testid="people-teams-page">
      {/* Header */}
      <div className="page-head">
        <div>
          <h1 data-testid="org-name">{org.company.name}</h1>
          <p className="sub">
            {members.length} {members.length === 1 ? 'person' : 'people'} · {teams.length} {teams.length === 1 ? 'team' : 'teams'} · {org.seats_used} {org.seats_used === 1 ? 'seat' : 'seats'} used
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full"
          style={{ background: roleBadge(org.global_role).bg, color: roleBadge(org.global_role).color }}>
          <Shield size={12} />{isAdmin ? 'Company Admin' : 'Member'}
        </span>
      </div>

      {/* Company execution overview (Admin) */}
      {isAdmin && reportError && (
        <div className="list-card" style={{ padding: '14px 16px', marginBottom: 18, color: '#B4332E', fontSize: 13 }} data-testid="company-report-error">
          The company execution overview couldn't load. Refresh to try again.
        </div>
      )}
      {isAdmin && report && (
        <>
          <div className="stats" data-testid="company-metrics">
            <div className="stat blue"><div className="v">{report.overall.open ?? 0}</div><div className="k">Open</div></div>
            <div className="stat bad"><div className="v">{report.overall.overdue ?? 0}</div><div className="k">Overdue</div></div>
            <div className="stat blue"><div className="v">{report.overall.due_this_week ?? 0}</div><div className="k">Due this week</div></div>
            <div className="stat ok"><div className="v">{report.overall.completed ?? 0}</div><div className="k">Completed</div></div>
            <div className="stat ok"><div className="v">{report.overall.completion_rate ?? 0}%</div><div className="k">Completion</div>
              <div className="bar"><i style={{ width: `${report.overall.completion_rate ?? 0}%` }} /></div></div>
          </div>
          {report.teams.length > 0 && (
            <div className="list-card" style={{ overflow: 'hidden', marginBottom: 22 }} data-testid="team-breakdown-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide" style={{ color: 'var(--faint)', borderBottom: '1px solid var(--line2)' }}>
                    <th className="px-4 py-2.5 font-bold">Team</th>
                    <th className="px-3 py-2.5 font-bold text-right">Open</th>
                    <th className="px-3 py-2.5 font-bold text-right">Overdue</th>
                    <th className="px-3 py-2.5 font-bold text-right">Completed</th>
                    <th className="px-4 py-2.5 font-bold text-right">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {report.teams.map(t => (
                    <tr key={t.team_id} className="cursor-pointer" style={{ borderTop: '1px solid var(--line2)' }}
                      onClick={() => openTeam(t.team_id)} data-testid={`team-row-${t.team_id}`}>
                      <td className="px-4 py-2.5 font-semibold">{t.name}</td>
                      <td className="px-3 py-2.5 text-right">{t.open}</td>
                      <td className="px-3 py-2.5 text-right" style={{ color: t.overdue ? 'var(--clay-tx)' : undefined }}>{t.overdue}</td>
                      <td className="px-3 py-2.5 text-right">{t.completed}</td>
                      <td className="px-4 py-2.5 text-right font-semibold" style={{ color: 'var(--green-tx)' }}>{t.completion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="layout">
        {/* Teams column */}
        <div className="col-main">
          <div className="sec-row">
            <div className="sh"><h3>Teams</h3><span className="n">({teams.length})</span></div>
            {isAdmin && (
              <button className="link" onClick={() => setShowTeamForm(v => !v)} data-testid="new-team-button">
                <Plus size={13} />New team
              </button>
            )}
          </div>

          {showTeamForm && isAdmin && (
            <div className="rail-card" style={{ marginBottom: 14 }} data-testid="team-form">
              <input value={teamForm.name} onChange={e => setTeamForm(p => ({ ...p, name: e.target.value }))} placeholder="Team name (e.g. Marketing)"
                data-testid="team-name-input" className="inv-input" />
              <input value={teamForm.description} onChange={e => setTeamForm(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)"
                data-testid="team-desc-input" className="inv-input" />
              <label className="flex items-center gap-2 text-xs cursor-pointer mb-3" style={{ color: 'var(--mute)' }}>
                <input type="checkbox" checked={teamForm.allow_member_cross_visibility} data-testid="team-crossvis-toggle"
                  onChange={e => setTeamForm(p => ({ ...p, allow_member_cross_visibility: e.target.checked }))} />
                Allow team members to see each other's Action Cards (default: off)
              </label>
              <div className="flex gap-2">
                <button onClick={createTeam} disabled={creatingTeam || !teamForm.name.trim()} data-testid="save-team-button"
                  className="btn-green inline-flex items-center gap-2">
                  {creatingTeam ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}Create team
                </button>
                <button onClick={() => setShowTeamForm(false)} className="btn-plain">Cancel</button>
              </div>
            </div>
          )}

          {teams.length === 0 ? (
            <div className="list-card"><div className="home-empty" data-testid="teams-empty">
              <div className="eic"><Users size={20} /></div>
              <div className="et">No teams yet</div>
              <div className="es">{isAdmin ? 'Create one to organize visibility & accountability.' : 'Ask an admin to add you to a team.'}</div>
            </div></div>
          ) : (
            <div className="team-list">
              {teams.map(t => (
                <button key={t.id} onClick={() => openTeam(t.id)} data-testid={`team-card-${t.id}`} className="team-card">
                  <div className="t-ic"><Users /></div>
                  <div className="t-body">
                    <div className="t-nm">{t.name}</div>
                    {t.description && <div className="t-desc">{t.description}</div>}
                    <div className="t-meta">{t.member_count} {t.member_count === 1 ? 'member' : 'members'}{t.allow_member_cross_visibility ? ' · shared visibility' : ''}</div>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--faint)', flex: 'none' }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Rail: invite + seats + people */}
        <aside className="col-rail" style={{ width: 320 }}>
          {isAdmin && (
            <div className="rail-card" data-testid="invite-panel">
              <div className="rc-title"><UserPlus />Invite people</div>
              <input value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} placeholder="Email address" type="email"
                data-testid="invite-email-input" className="inv-input" />
              <div className="inv-row2">
                <select value={inviteForm.global_role} onChange={e => setInviteForm(p => ({ ...p, global_role: e.target.value }))} data-testid="invite-role-select">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <select value={inviteForm.team_id} onChange={e => setInviteForm(p => ({ ...p, team_id: e.target.value }))} data-testid="invite-team-select">
                  <option value="">No team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <button onClick={sendInvite} disabled={inviting || !inviteForm.email.trim()} data-testid="send-invite-button"
                className="btn-green w-full inline-flex items-center justify-center gap-2">
                {inviting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}Send invite
              </button>
              {invites.length > 0 && (
                <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid var(--line2)' }} data-testid="pending-org-invites">
                  <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--faint)' }}>Pending invites</p>
                  {invites.map(i => (
                    <div key={i.id} className="flex items-center justify-between text-xs" data-testid={`org-invite-${i.id}`}>
                      <span className="truncate" style={{ color: 'var(--mute)' }}>{i.email} · {i.global_role}</span>
                      <button onClick={() => revokeInvite(i.id)} className="p-1" style={{ color: 'var(--clay-tx)' }} data-testid={`revoke-invite-${i.id}`}><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="rail-card" data-testid="members-panel">
            <div className="rc-title"><Users />People ({members.length})</div>
            <div className="space-y-2.5 max-h-[420px] overflow-y-auto">
              {members.map(m => {
                const rb = roleBadge(m.global_role);
                return (
                  <div key={m.id} className="flex items-center gap-3" data-testid={`member-${m.id}`}>
                    <Avatar name={m.name} picture={m.picture} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{m.name}{m.id === user?.id ? ' (You)' : ''}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--faint)' }}>{m.email}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 h-5 rounded-full" style={{ background: rb.bg, color: rb.color }}>
                      <rb.Icon size={10} />{rb.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      {/* Team detail drawer */}
      {selectedTeam && (
        <TeamDrawer team={selectedTeam} members={members} isAdmin={isAdmin}
          onClose={() => setSelectedTeam(null)} onAddMember={addMember} onRemoveMember={removeMember} onDelete={deleteTeam} />
      )}
    </div>
  );
}

function TeamDrawer({ team, members, isAdmin, onClose, onAddMember, onRemoveMember, onDelete }) {
  const navigate = useNavigate();
  const [addUser, setAddUser] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [report, setReport] = useState(null);
  const memberIds = new Set((team.members || []).map(m => m.id));
  const available = members.filter(m => !memberIds.has(m.id));

  useEffect(() => {
    setReport(null);
    organizationApi.teamReport(team.id).then(r => setReport(r.data)).catch(() => setReport(false));
  }, [team.id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="team-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl overflow-y-auto">
        <div className="p-5 border-b border-border flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold serif">{team.name}</h2>
            {team.description && <p className="text-xs text-muted-foreground mt-0.5">{team.description}</p>}
            <p className="text-[11px] text-muted-foreground mt-1">{team.allow_member_cross_visibility ? 'Members can see each other\'s cards' : 'Member cards private by default'}</p>
          </div>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="close-team-drawer"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {report && report.metrics && (
            <div className="space-y-3" data-testid="team-report">
              {report.needs_attention && report.needs_attention.length > 0 && (
                <div className="rounded-xl border p-3" style={{ borderColor: 'rgba(180,51,46,0.25)', background: 'rgba(180,51,46,0.04)' }} data-testid="needs-attention">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} style={{ color: '#B4332E' }} />
                    <span className="text-xs font-semibold" style={{ color: '#B4332E' }}>Needs attention ({report.needs_attention.length})</span>
                  </div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {report.needs_attention.map((t, i) => (
                      <button key={t.id || i} onClick={() => t.action_card_id && navigate(`/action-cards/${t.action_card_id}`)}
                        data-testid="attention-row"
                        className="w-full text-left flex items-center justify-between gap-2 p-2 rounded-lg bg-white border border-border hover:border-[#B4332E]/40 transition-colors">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{t.title}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{t.assignee_name}{t.action_card_title ? ` · ${t.action_card_title}` : ''}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {t.reasons.map(r => (
                            <span key={r} className="text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
                              style={r === 'overdue' ? { background: 'rgba(180,51,46,0.12)', color: '#B4332E' }
                                : { background: 'rgba(105,148,198,0.14)', color: '#4f74a0' }}>
                              {r === 'low_confidence' ? 'low conf' : r}
                            </span>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <MetricsStrip m={report.metrics} testId="team-metrics" />
              {report.metrics.by_assignee.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Workload by person</p>
                  <div className="space-y-1.5">
                    {report.metrics.by_assignee.map((a, i) => (
                      <div key={a.user_id || i} className="flex items-center justify-between text-sm py-1" data-testid="workload-row">
                        <span className="truncate">{a.name}</span>
                        <span className="flex items-center gap-3 text-xs shrink-0">
                          <span title="Open">{a.open} open</span>
                          {a.overdue > 0 && <span style={{ color: '#B4332E' }} title="Overdue">{a.overdue} overdue</span>}
                          <span className="text-muted-foreground" title="Completed">{a.completed} done</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="h-px bg-border" />
            </div>
          )}

          {isAdmin && available.length > 0 && (
            <div className="p-3 rounded-xl bg-secondary/50 border border-border space-y-2" data-testid="add-member-form">
              <p className="text-xs font-medium">Add a member</p>
              <div className="flex gap-2">
                <select value={addUser} onChange={e => setAddUser(e.target.value)} data-testid="add-member-select"
                  className="flex-1 h-9 rounded-lg border border-border px-2 text-sm bg-white">
                  <option value="">Select person…</option>
                  {available.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <select value={addRole} onChange={e => setAddRole(e.target.value)} data-testid="add-member-role"
                  className="h-9 rounded-lg border border-border px-2 text-sm bg-white">
                  <option value="member">Member</option>
                  <option value="manager">Manager</option>
                </select>
                <button disabled={!addUser} onClick={() => { onAddMember(addUser, addRole); setAddUser(''); }}
                  data-testid="confirm-add-member" className="h-9 px-3 rounded-lg text-sm text-white disabled:opacity-50" style={{ background: '#6994c6' }}>Add</button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {(team.members || []).length === 0 && <p className="text-sm text-muted-foreground" data-testid="team-no-members">No members yet.</p>}
            {(team.members || []).map(m => (
              <div key={m.id} className="flex items-center gap-3" data-testid={`team-member-${m.id}`}>
                <Avatar name={m.name} picture={m.picture} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                {m.team_role === 'manager' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 h-5 rounded-full" style={{ background: 'rgba(212,164,74,0.15)', color: '#b0842f' }}><Crown size={10} />Manager</span>
                )}
                {isAdmin && (
                  <button onClick={() => onRemoveMember(m.id)} className="text-red-500 hover:text-red-600 p-1" data-testid={`remove-member-${m.id}`}><Trash2 size={13} /></button>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <button onClick={() => onDelete(team.id)} data-testid="delete-team-button"
              className="w-full h-9 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 flex items-center justify-center gap-2 mt-6">
              <Trash2 size={14} />Archive team
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
